import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { MapPin, ArrowLeft, Loader2 } from "lucide-react";
import api from "@/lib/api";
import ProviderCard from "@/components/ProviderCard";
import ProvidersMap from "@/components/ProvidersMap";
import SEO from "@/components/SEO";

export default function StatePage() {
  const { state } = useParams();
  const [data, setData] = useState(null);

  useEffect(() => {
    setData(null);
    api.get(`/seo/state/${state}`).then(({ data }) => setData(data));
    window.scrollTo({ top: 0 });
  }, [state]);

  if (!data) {
    return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#064E3B]" /></div>;
  }

  const stateName = data.state;
  const stateSlug = state;
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: `IV Therapy Providers in ${stateName}`,
    numberOfItems: data.total,
    itemListElement: data.providers.slice(0, 20).map((p, i) => ({
      "@type": "ListItem",
      position: i + 1,
      item: {
        "@type": "MedicalBusiness",
        name: p.name,
        address: { "@type": "PostalAddress", addressLocality: p.city, addressRegion: p.state, postalCode: p.zip_code },
        telephone: p.phone,
        aggregateRating: p.review_count > 0 ? {
          "@type": "AggregateRating", ratingValue: p.avg_rating, reviewCount: p.review_count
        } : undefined,
      },
    })),
  };

  return (
    <div className="min-h-screen">
      <SEO
        title={`IV Therapy in ${stateName} — ${data.total} Verified Providers | IV Therapy Finder`}
        description={`Find ${data.total} trusted IV therapy clinics and mobile infusion providers across ${stateName}. Compare reviews, services, and book appointments.`}
        jsonLd={jsonLd}
      />

      <section className="bg-gradient-to-b from-[#F3F4F1] to-[#FAFAFA] border-b border-stone-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <Link to="/" className="text-xs text-stone-500 hover:text-[#064E3B] inline-flex items-center gap-1 mb-3"><ArrowLeft className="w-3 h-3" /> All states</Link>
          <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight text-stone-900 mb-3">
            IV Therapy Providers in {stateName}
          </h1>
          <p className="text-stone-600 max-w-2xl">
            {data.total} verified IV therapy clinics and mobile infusion providers across {data.cities.length} {data.cities.length === 1 ? "city" : "cities"} in {stateName}.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <h2 className="font-display text-2xl font-semibold mb-5">Cities in {stateName}</h2>
        <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mb-12">
          {data.cities.map((c) => (
            <Link
              key={c.city}
              to={`/cities/${stateSlug}/${c.city.toLowerCase().replace(/\s+/g, "-")}/iv-therapy`}
              className="bg-white rounded-xl border border-stone-200 p-4 hover:border-[#064E3B]/40 hover:shadow-md transition"
              data-testid={`state-city-${c.city.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <div className="font-display font-semibold text-stone-900">{c.city}</div>
              <div className="text-xs text-[#10B981] font-semibold uppercase tracking-wider mt-1">{c.count} provider{c.count !== 1 ? "s" : ""}</div>
            </Link>
          ))}
        </div>

        {data.providers.length > 0 && (
          <>
            <h2 className="font-display text-2xl font-semibold mb-5">All providers</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {data.providers.map((p) => <ProviderCard key={p.id} p={p} />)}
            </div>
          </>
        )}
      </section>
    </div>
  );
}
